import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, ErrorState, Loading, PageHeader, StatCard } from '@/components/ui';

/**
 * What this box is, and how much of it is left.
 *
 * Every number here was already on the machine and reachable only by SSH. On a
 * self-hosted platform that runs on one small box, "is it out of disk" and "is
 * it out of CPU" are the first two questions when anything is slow, and they
 * were the two questions this product could not answer about itself.
 */

interface Info {
  host: string;
  os: string;
  os_pretty?: string;
  python: string;
  version: string;
  cpus: number | null;
  uptime_sec?: number;
  load?: { '1m': number; '5m': number; '15m': number; per_cpu_pct: number };
  memory?: {
    total_bytes: number;
    available_bytes: number;
    used_pct: number | null;
    swap_total_bytes: number;
    swap_free_bytes: number;
  };
  disk?: { total_bytes: number; free_bytes: number; used_pct: number | null };
  voice_cache?: Record<string, number>;
  api_process?: { pid: number; rss_bytes: number };
}

function bytes(value: number | undefined | null): string {
  if (!value) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let n = value;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function uptime(seconds: number | undefined): string {
  if (!seconds) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days) return `${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
}

/** Green below half, amber past three quarters, red past ninety. */
function pressure(pct: number | null | undefined): 'default' | 'warn' | 'bad' {
  if (pct === null || pct === undefined) return 'default';
  if (pct >= 90) return 'bad';
  if (pct >= 75) return 'warn';
  return 'default';
}

export default function SystemInfo() {
  const info = useQuery({
    queryKey: ['system-info'],
    queryFn: () => api.get<Info>('/admin/system-info'),
    refetchInterval: 15_000,
  });

  if (info.isPending) return <Loading />;
  if (info.error) return <ErrorState error={info.error} retry={() => info.refetch()} />;

  const data = info.data;

  return (
    <div className="w-full">
      <PageHeader
        title="System"
        description={`${data.os_pretty ?? data.os} · ${data.host}`}
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="CPU load"
          value={data.load ? `${data.load.per_cpu_pct}%` : '—'}
          sub={
            data.load
              ? `${data.load['1m']} / ${data.load['5m']} / ${data.load['15m']} over ${data.cpus} cores`
              : 'not readable here'
          }
          tone={pressure(data.load?.per_cpu_pct)}
          dense
        />
        <StatCard
          label="Memory"
          value={data.memory?.used_pct !== null ? `${data.memory?.used_pct}%` : '—'}
          sub={
            data.memory
              ? `${bytes(data.memory.available_bytes)} free of ${bytes(data.memory.total_bytes)}`
              : undefined
          }
          tone={pressure(data.memory?.used_pct)}
          dense
        />
        <StatCard
          label="Disk"
          value={data.disk?.used_pct !== null ? `${data.disk?.used_pct}%` : '—'}
          sub={
            data.disk
              ? `${bytes(data.disk.free_bytes)} free of ${bytes(data.disk.total_bytes)}`
              : undefined
          }
          tone={pressure(data.disk?.used_pct)}
          dense
        />
        <StatCard label="Uptime" value={uptime(data.uptime_sec)} dense />
      </div>

      {/* Load is the one number people read wrong, so it says what it means
          rather than leaving it to be looked up. */}
      {data.load && data.load.per_cpu_pct >= 75 && (
        <p className="mt-3 rounded-md bg-amber-50 p-3 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <strong>This box is busy.</strong> Load is measured against{' '}
          {data.cpus} cores, and the API runs every live call on a single event
          loop — so a saturated box shows up first as audio breaking up, not as
          an error. <code className="font-mono">glee-capacity</code> will say
          how many concurrent calls it can still pace.
        </p>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card title="This box">
          <dl className="space-y-2 text-sm">
            {[
              ['Glee version', data.version],
              ['Hostname', data.host],
              ['Operating system', data.os_pretty ?? data.os],
              ['Kernel', data.os],
              ['Python', data.python],
              ['CPU cores', String(data.cpus ?? '—')],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt className="text-ink-500">{label}</dt>
                <dd className="truncate text-right text-ink-800 dark:text-ink-100">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </Card>

        <Card title="What is using it">
          <dl className="space-y-2 text-sm">
            {[
              [
                'API process',
                data.api_process
                  ? `${bytes(data.api_process.rss_bytes)} (pid ${data.api_process.pid})`
                  : '—',
              ],
              [
                'Voice cache on disk',
                data.voice_cache ? bytes(data.voice_cache.bytes) : '—',
              ],
              [
                'Voice cache files',
                data.voice_cache ? String(data.voice_cache.files ?? '—') : '—',
              ],
              [
                'Swap',
                data.memory?.swap_total_bytes
                  ? `${bytes(
                      data.memory.swap_total_bytes - data.memory.swap_free_bytes,
                    )} of ${bytes(data.memory.swap_total_bytes)} used`
                  : 'none configured',
              ],
            ].map(([label, value]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt className="text-ink-500">{label}</dt>
                <dd className="truncate text-right text-ink-800 dark:text-ink-100">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-2xs text-ink-500">
            The voice cache is the one directory here that grows without anybody
            deciding to grow it. It can be emptied from the Voice cache page.
          </p>
        </Card>
      </div>
    </div>
  );
}
